import { useEffect, useRef, useState } from 'react'
import { InboundMessage } from 'ably'
import { useWebSocket } from '@/contexts/WebSocketContext'
import { api } from '@/lib/axios'

type ChunkPayload = { msgRef: string; delta: string; index: number }
type DonePayload = { msgRef: string; finalContent: string; spentValue: number; responseId: string }
type FailPayload = { msgRef: string; error: string }
type StoppedPayload = { msgRef: string; finalContent: string }

type Status = 'idle' | 'streaming' | 'done' | 'failed'

export type ChatStreamCallbacks = {
  onChunkStart?: (msgRef: string) => void
  onComplete?: (payload: DonePayload) => void
  onFail?: (payload: FailPayload) => void
  onStopped?: (payload: StoppedPayload) => void
}

export function useChatStream(params: {
  userRef: string | null
  callbacks?: ChatStreamCallbacks
}) {
  const { userRef, callbacks } = params
  const { ws } = useWebSocket()
  const [streamingMsgRef, setStreamingMsgRef] = useState<string | null>(null)
  const [streamingDelta, setStreamingDelta] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const indexBufferRef = useRef<Map<number, string>>(new Map())

  useEffect(() => {
    if (!ws || !userRef) return
    const channelName = `nina-chat-${userRef}`
    const channel = ws.channels.get(channelName)

    const onChunk = (msg: InboundMessage) => {
      const { msgRef, delta, index } = msg.data as ChunkPayload
      setStreamingMsgRef(prev => {
        if (prev !== msgRef) callbacks?.onChunkStart?.(msgRef)
        return msgRef
      })
      indexBufferRef.current.set(index, delta)
      const ordered = Array.from(indexBufferRef.current.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v)
        .join('')
      setStreamingDelta(ordered)
      setStatus('streaming')
    }

    const onDone = (msg: InboundMessage) => {
      setStatus('done')
      callbacks?.onComplete?.(msg.data as DonePayload)
      setTimeout(() => {
        setStreamingMsgRef(null)
        setStreamingDelta('')
        indexBufferRef.current.clear()
        setStatus('idle')
      }, 100)
    }

    const onFail = (msg: InboundMessage) => {
      setStatus('failed')
      callbacks?.onFail?.(msg.data as FailPayload)
      setTimeout(() => {
        setStreamingMsgRef(null)
        setStreamingDelta('')
        indexBufferRef.current.clear()
        setStatus('idle')
      }, 200)
    }

    const onStopped = (msg: InboundMessage) => {
      setStatus('done')
      callbacks?.onStopped?.(msg.data as StoppedPayload)
      setTimeout(() => {
        setStreamingMsgRef(null)
        setStreamingDelta('')
        indexBufferRef.current.clear()
        setStatus('idle')
      }, 100)
    }

    channel.subscribe('NINA_CHAT_CHUNK', onChunk)
    channel.subscribe('NINA_CHAT_DONE', onDone)
    channel.subscribe('NINA_CHAT_FAIL', onFail)
    channel.subscribe('NINA_CHAT_STOPPED', onStopped)

    return () => {
      channel.unsubscribe('NINA_CHAT_CHUNK', onChunk)
      channel.unsubscribe('NINA_CHAT_DONE', onDone)
      channel.unsubscribe('NINA_CHAT_FAIL', onFail)
      channel.unsubscribe('NINA_CHAT_STOPPED', onStopped)
    }
  }, [ws, userRef, callbacks])

  const requestStop = async (msgRef: string) => {
    if (!userRef || !msgRef) return
    try {
      await api.post('/', { action: 'stop-stream', userRef, msgRef })
    } catch (err) {
      console.error('[useChatStream] Stop request failed', err)
    }
  }

  return { streamingMsgRef, streamingDelta, status, requestStop }
}
